import styles from 'css/src/components/app/chat-tab/components/ToolsModal.module.scss';

import React from 'react';

import { humanizeId } from './humanizeId';

import type { ToolDefinition } from 'js/src/agent';
import type { ToolChildConversationSettings } from 'js/src/agent/toolChildConversationSettings';
import type { Model, ReasoningEffort } from 'js/src/agent/types';
import { AVAILABLE_MODELS, reasoningEffortOptionsForModel } from 'js/src/agent/types';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import Select from 'js/src/components/common/ui/Select/Select';
import type { SelectOption } from 'js/src/components/common/ui/Select/types';
import type { TypeInputChangeEvent } from 'js/src/components/common/ui/TextInput';
import TextInput from 'js/src/components/common/ui/TextInput';

interface ParameterSchema {
    type?: string;
    description?: string;
    enum?: string[];
    properties?: Record<string, ParameterSchema>;
    required?: string[];
    items?: ParameterSchema;
    [key: string]: unknown;
}

interface ToolsModalProps {
    showing: boolean;
    onHide: VoidFunction;
    tools: ToolDefinition[] | null | undefined;
    // Maps a tool's function name to its Material Symbols status icon, so each
    // tile can show the same icon used in the running status line beside its name.
    iconForTool?: (name: string) => string | undefined;
    // Maps a tool's function name to the model and reasoning effort its child
    // conversation runs at, for the tools that spawn one. Undefined for the rest,
    // which get no such controls.
    childConversationSettingsForTool?: (name: string) => ToolChildConversationSettings | undefined;
}

const NestedParameters: React.FC<{ schema: ParameterSchema }> = ({ schema }) => {
    const entries = Object.entries(schema.properties ?? {});
    if (entries.length === 0) {
        return null;
    }
    return (
        <div className={`mt-l5-4 d-flex flex-column ${styles['parameters']}`}>
            {entries.map(([childName, childSchema]) => (
                <ParameterRow
                    key={childName}
                    name={childName}
                    schema={childSchema}
                    required={!!schema.required?.includes(childName)}
                />
            ))}
        </div>
    );
};

const ParameterRow: React.FC<{
    name: string;
    schema: ParameterSchema;
    required: boolean;
}> = ({ name, schema, required }) => {
    const values = schema.enum ?? [];
    // An array's shape lives on its `items`; show the element type alongside the
    // "array" badge (e.g. "array of object") and descend into it below.
    const itemSchema = schema.type === 'array' ? schema.items : undefined;
    // The object whose nested fields we should render: this param itself if it's
    // an object, or an array's element schema if that element is an object.
    const objectSchema =
        schema.type === 'object' ? schema : itemSchema?.type === 'object' ? itemSchema : undefined;

    return (
        <div className="text-break">
            <div className="d-flex align-items-center gap-2 flex-wrap">
                <code className={`color-primary ${styles['parameter-name']}`}>{name}</code>
                {required && <span className="badge bg-warning text-dark">required</span>}
                {schema.type && (
                    <span className="badge bg-info text-dark">
                        {itemSchema?.type ? `${schema.type} of ${itemSchema.type}` : schema.type}
                    </span>
                )}
            </div>
            {schema.description && (
                <div className={`color-grey-600 mt-l5-2 ${styles['parameter-description']}`}>
                    {schema.description}
                </div>
            )}
            {itemSchema?.description && (
                <div className={`color-grey-600 mt-l5-2 ${styles['parameter-description']}`}>
                    {itemSchema.description}
                </div>
            )}
            {values.length > 0 && (
                <div className={`${styles['parameter-values']} mt-l5-2`}>
                    <span className="text-muted">Allowed values:</span>{' '}
                    <code>{values.join(', ')}</code>
                </div>
            )}
            {objectSchema && <NestedParameters schema={objectSchema} />}
        </div>
    );
};

const MODEL_OPTIONS: SelectOption<Model>[] = AVAILABLE_MODELS.map(({ value, label }) => ({
    value,
    label,
}));

/**
 * Model and reasoning-effort pickers for a tool that spawns a child conversation.
 * Changing either takes effect on the tool's next call — these write straight to the
 * live tool instance — and resets on reload, since the code declaration is the default.
 */
const ChildConversationControls: React.FC<{
    toolName: string;
    settings: ToolChildConversationSettings;
}> = ({ toolName, settings }) => {
    const effortOptions: SelectOption<ReasoningEffort>[] = reasoningEffortOptionsForModel(
        settings.model,
    ).map(({ value, label }) => ({ value, label }));

    return (
        <div className="mb-l5-16">
            <div
                className={`text-uppercase fw-semibold color-grey-500 mb-l5-8 ${styles['parameters-heading']}`}
            >
                Child conversation model
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
                <Select<Model>
                    aria-label={`Model for ${toolName}`}
                    className={styles['child-conversation-picker']}
                    isMulti={false}
                    options={MODEL_OPTIONS}
                    value={MODEL_OPTIONS.find((option) => option.value === settings.model)}
                    onChange={settings.setModel}
                />
                {effortOptions.length > 0 && (
                    <Select<ReasoningEffort>
                        aria-label={`Reasoning effort for ${toolName}`}
                        className={styles['child-conversation-picker']}
                        isMulti={false}
                        options={effortOptions}
                        value={effortOptions.find(
                            (option) => option.value === settings.reasoningEffort,
                        )}
                        onChange={settings.setReasoningEffort}
                    />
                )}
                {settings.isOverridden && (
                    <button
                        type="button"
                        className="btn btn-link p-l5-0 text-decoration-none color-grey-500"
                        onClick={settings.reset}
                    >
                        reset
                    </button>
                )}
            </div>
        </div>
    );
};

// A collapsed tool tile shows only the first few lines of its content; the rest is revealed on
// click. This keeps tools with many parameters (or long enum lists) from dominating the modal.
const COLLAPSED_MAX_HEIGHT = 220;

const ToolTile: React.FC<{
    tool: ToolDefinition;
    icon?: string;
    childConversationSettings?: ToolChildConversationSettings;
}> = ({ tool, icon, childConversationSettings }) => {
    const [expanded, setExpanded] = React.useState(false);
    const [isOverflowing, setIsOverflowing] = React.useState(false);
    const contentRef = React.useRef<HTMLDivElement | null>(null);

    const { name, description, parameters } = tool.function;
    const parameterEntries = Object.entries(parameters.properties ?? {}) as Array<
        [string, ParameterSchema]
    >;

    // Decide whether to offer "Show more" from the content's actual rendered
    // height, not the number of parameters: a tool with few params but long
    // wrapping enum lists can still overflow, and one with many short params may
    // not. scrollHeight reflects full content even while clamped, and a
    // ResizeObserver re-checks on any reflow (wrapping, resize, font load).
    React.useEffect(() => {
        const el = contentRef.current;
        if (!el) {
            return;
        }
        const measure = () => setIsOverflowing(el.scrollHeight > COLLAPSED_MAX_HEIGHT);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div className={`${styles['tool-tile']} rounded-3 p-l5-16`}>
            <div className="d-flex align-items-center gap-2 mb-l5-2">
                {icon && (
                    <i
                        className={`material-symbols-outlined color-grey-500 ${styles['tool-icon']}`}
                        aria-hidden
                    >
                        {icon}
                    </i>
                )}
                <span className={`fw-semibold ${styles['tool-name']}`}>{humanizeId(name)}</span>
            </div>
            <div className={`font-monospace color-grey-500 mb-l5-8 ${styles['tool-id']}`}>
                id: {name}
            </div>
            {description && (
                <div className={`color-grey-600 mb-l5-16 ${styles['tool-description']}`}>
                    {description}
                </div>
            )}
            {childConversationSettings && (
                <ChildConversationControls toolName={name} settings={childConversationSettings} />
            )}
            <div
                ref={contentRef}
                className="position-relative"
                style={
                    expanded ? undefined : { maxHeight: COLLAPSED_MAX_HEIGHT, overflow: 'hidden' }
                }
            >
                <div
                    className={`text-uppercase fw-semibold color-grey-500 mb-l5-8 ${styles['parameters-heading']}`}
                >
                    Parameters
                </div>
                {parameterEntries.length > 0 ? (
                    <div className={`d-flex flex-column ${styles['parameters']}`}>
                        {parameterEntries.map(([paramName, paramSchema]) => (
                            <ParameterRow
                                key={paramName}
                                name={paramName}
                                schema={paramSchema}
                                required={!!parameters.required?.includes(paramName)}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-muted small">No parameters</div>
                )}
            </div>
            {(isOverflowing || expanded) && (
                <div className="d-flex justify-content-center mt-l5-8">
                    <button
                        type="button"
                        className={`btn btn-outline-secondary btn-sm ${styles['show-more-button']}`}
                        onClick={() => setExpanded((prev) => !prev)}
                    >
                        {expanded ? 'Show less' : 'Show more'}
                    </button>
                </div>
            )}
        </div>
    );
};

const ToolsModal: React.FC<ToolsModalProps> = ({
    showing,
    onHide,
    tools,
    iconForTool,
    childConversationSettingsForTool,
}) => {
    const [query, setQuery] = React.useState('');

    const handleQueryChange = React.useCallback((e: TypeInputChangeEvent) => {
        setQuery(e.target.value);
    }, []);

    const filteredTools = React.useMemo(() => {
        const allTools = tools ?? [];
        const trimmed = query.trim().toLowerCase();
        if (!trimmed) {
            return allTools;
        }
        return allTools.filter((tool) => tool.function.name.toLowerCase().includes(trimmed));
    }, [tools, query]);

    return (
        <Modal showing={showing} onHidden={onHide} title="Tools" size={MODAL_SIZES.LG} scrollable>
            <div className={styles['search']}>
                <TextInput
                    text={query}
                    onChange={handleQueryChange}
                    placeholder="Search tools by name"
                    autoFocus
                />
            </div>
            {filteredTools.length > 0 ? (
                <div className={`d-flex flex-column ${styles['tool-list']}`}>
                    {filteredTools.map((tool) => (
                        <ToolTile
                            key={tool.function.name}
                            tool={tool}
                            icon={iconForTool?.(tool.function.name)}
                            childConversationSettings={childConversationSettingsForTool?.(
                                tool.function.name,
                            )}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-muted text-center py-l5-24">
                    {tools && tools.length > 0 ? `No tools match “${query}”` : 'No tools available'}
                </div>
            )}
        </Modal>
    );
};

export default ToolsModal;
